// Google Analytics 4 Enhanced Event Tracking for Iligan Weather Tracker

// Track weather data views
export const trackWeatherView = (location, weatherCondition, hasTyphooon = false) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'weather_view', {
      event_category: 'Weather Data',
      event_label: location,
      custom_parameters: {
        location: location,
        weather_condition: weatherCondition,
        has_typhoon: hasTyphooon,
        timestamp: new Date().toISOString()
      }
    });
  }
};

// Track typhoon alerts
export const trackTyphoonAlert = (typhoonName, signalLevel, affectedAreas) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'typhoon_alert', {
      event_category: 'Emergency Alert',
      event_label: `${typhoonName} - Signal ${signalLevel}`,
      value: parseInt(signalLevel) || 1,
      custom_parameters: {
        typhoon_name: typhoonName,
        signal_level: signalLevel,
        affected_areas: affectedAreas,
        alert_time: new Date().toISOString()
      }
    });
  }
};

// Track barangay-specific weather checks
export const trackBarangayWeather = (barangayName, weatherCondition, floodRisk) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'barangay_weather_check', {
      event_category: 'Local Weather',
      event_label: `Brgy. ${barangayName}`,
      custom_parameters: {
        barangay: barangayName,
        weather_condition: weatherCondition,
        flood_risk: floodRisk,
        check_time: new Date().toISOString()
      }
    });
  }
};

// Track map interactions
export const trackMapInteraction = (mapType, action, location = 'Iligan City') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'map_interaction', {
      event_category: 'Interactive Maps',
      event_label: `${mapType} - ${action}`,
      custom_parameters: {
        map_type: mapType,
        action: action,
        location: location,
        interaction_time: new Date().toISOString()
      }
    });
  }
};

// Track emergency contact usage
export const trackEmergencyContact = (contactType, contactNumber) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'emergency_contact', {
      event_category: 'Emergency Response',
      event_label: contactType,
      custom_parameters: {
        contact_type: contactType,
        contact_number: contactNumber,
        contact_time: new Date().toISOString()
      }
    });
  }
};

// Track data refresh attempts
export const trackDataRefresh = (source, success, errorMessage = null) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'data_refresh', {
      event_category: 'Data Management',
      event_label: source,
      value: success ? 1 : 0,
      custom_parameters: {
        data_source: source,
        success: success,
        error_message: errorMessage,
        refresh_time: new Date().toISOString()
      }
    });
  }
};

// Track search queries (if implementing search)
export const trackWeatherSearch = (searchQuery, resultsFound) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'search', {
      search_term: searchQuery,
      event_category: 'Site Search',
      custom_parameters: {
        search_query: searchQuery,
        results_found: resultsFound,
        search_time: new Date().toISOString()
      }
    });
  }
};

// Track user engagement with 5-day outlook
export const trackOutlookView = (days, location) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'outlook_view', {
      event_category: 'Weather Forecast',
      event_label: `${days}-day outlook - ${location}`,
      custom_parameters: {
        forecast_days: days,
        location: location,
        view_time: new Date().toISOString()
      }
    });
  }
};

// Track external link clicks (GoIligan, PAGASA, etc.)
export const trackExternalLink = (linkUrl, linkText, category = 'External Links') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'click', {
      event_category: category,
      event_label: linkText,
      custom_parameters: {
        link_url: linkUrl,
        link_text: linkText,
        click_time: new Date().toISOString()
      }
    });
  }
};

// Track page performance metrics
export const trackPerformance = (loadTime, dataFreshness) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'performance_metrics', {
      event_category: 'Site Performance',
      event_label: 'Page Load',
      value: Math.round(loadTime),
      custom_parameters: {
        load_time_ms: loadTime,
        data_freshness_minutes: dataFreshness,
        performance_time: new Date().toISOString()
      }
    });
  }
};

// Track user location (if geolocation is used)
export const trackUserLocation = (detectedBarangay, accuracy) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'location_detection', {
      event_category: 'Geolocation',
      event_label: detectedBarangay || 'Unknown',
      custom_parameters: {
        detected_barangay: detectedBarangay,
        accuracy_meters: accuracy,
        detection_time: new Date().toISOString()
      }
    });
  }
};

// Enhanced social sharing tracking with detailed metrics
export const trackSocialShare = (platform, contentType, weatherCondition, shareSuccess = true) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'share', {
      method: platform,
      content_type: contentType,
      event_category: 'Social Sharing',
      value: shareSuccess ? 1 : 0,
      custom_parameters: {
        platform: platform,
        content_type: contentType,
        weather_condition: weatherCondition || 'unknown',
        share_success: shareSuccess,
        share_time: new Date().toISOString(),
        user_agent: navigator.userAgent,
        share_location: 'Iligan City'
      }
    });
  }

  // Also track as conversion event for important shares (typhoon alerts)
  if (contentType === 'typhoon' && shareSuccess) {
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'emergency_share_conversion', {
        event_category: 'Emergency Response',
        event_label: `${platform} - Typhoon Alert Shared`,
        value: 1,
        custom_parameters: {
          conversion_type: 'emergency_alert_shared',
          platform: platform,
          alert_type: 'typhoon',
          share_time: new Date().toISOString()
        }
      });
    }
  }
};

// Track social share button clicks (before actual sharing)
export const trackShareButtonClick = (platform, contentType, location = 'Iligan City') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'share_button_click', {
      event_category: 'Social Sharing',
      event_label: `${platform} - ${contentType}`,
      custom_parameters: {
        platform: platform,
        content_type: contentType,
        location: location,
        click_time: new Date().toISOString(),
        user_intent: 'share_initiation'
      }
    });
  }
};

// Track share modal interactions
export const trackShareModal = (action, contentType = 'weather') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'share_modal_interaction', {
      event_category: 'User Interface',
      event_label: `Share Modal - ${action}`,
      custom_parameters: {
        modal_action: action, // 'open', 'close', 'type_change'
        content_type: contentType,
        interaction_time: new Date().toISOString()
      }
    });
  }
};

// Track copy to clipboard success/failure
export const trackClipboardAction = (success, contentType = 'weather') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'clipboard_action', {
      event_category: 'User Interaction',
      event_label: success ? 'Copy Success' : 'Copy Failed',
      value: success ? 1 : 0,
      custom_parameters: {
        action_success: success,
        content_type: contentType,
        action_time: new Date().toISOString(),
        method: 'clipboard_api'
      }
    });
  }
};

// Track image download for sharing
export const trackImageDownload = (contentType = 'weather', imageFormat = 'png') => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'image_download', {
      event_category: 'Content Download',
      event_label: `Weather Image - ${contentType}`,
      custom_parameters: {
        download_type: 'weather_image',
        content_type: contentType,
        image_format: imageFormat,
        download_time: new Date().toISOString(),
        download_source: 'share_modal'
      }
    });
  }
};

export default {
  trackWeatherView,
  trackTyphoonAlert,
  trackBarangayWeather,
  trackMapInteraction,
  trackEmergencyContact,
  trackDataRefresh,
  trackWeatherSearch,
  trackOutlookView,
  trackExternalLink,
  trackPerformance,
  trackUserLocation,
  trackSocialShare,
  trackShareButtonClick,
  trackShareModal,
  trackClipboardAction,
  trackImageDownload
};